import { DrawerPlay24Filled } from '@fluentui/react-icons';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import i18n, { SettingsType } from 'utils/i18n';

enum StatusTypes {
  inProgress = 'statusInProgress',
  notStarted = 'statusNotStarted',
  completed = 'statusCompleted',
}

export type XPHexagonProps = {
  status: string;
  delay?: boolean;
};

export const ProgressHexagon: React.FC<XPHexagonProps> = ({ status, delay }) => {
  const { locale } = useRouter();
  const { dictionary } = i18n[(locale as AcceptedLocales) || 'en-US'] as SettingsType;

  return (
    <StyledXPHexagon delay={delay} className={`xp-hexagon ${status === StatusTypes.completed ? 'is-completed' : ''}`}>
      <div className="xp-wrapper">
        {status === StatusTypes.completed && <span className="xp-tag-icon is-shown-complete docon docon-check"></span>}
        <span aria-hidden="true" className="xp high-contrast-text-invert">
          {dictionary[status]}
        </span>
      </div>
    </StyledXPHexagon>
  );
};

const getTransitionBackgroundColor = (delay: boolean | undefined) =>
  delay ? 'background-color 3s ease 0.3s' : 'background-color 0.3s ease 0.3s';

const getTransitionColor = (delay: boolean | undefined) => (delay ? 'color 3s ease 0.3s' : 'color 0.3s ease 0.3s');

const getTransitionBorderColor = (delay: boolean | undefined) =>
  delay ? 'border-color 3s ease 0.3s' : 'border-color 0.3s ease 0.3s';

export const StyledXPHexagon = styled.div<{ delay?: boolean }>`
  position: absolute;
  top: 0;
  right: 0;

  .xp-wrapper {
    background-color: var(--border);
    transition: background-color 0.8s ease;
    transition-delay: 0 0.4s;
    align-items: center;
    display: flex;
    height: 18px;
    justify-content: center;
    left: -1.5em;
    min-width: calc(100% + 1.5em);
    position: relative;

    &:before {
      background-color: var(--border);
      transition: background-color 0.8s ease;
      transition-delay: 0 0.4s;
      border: none !important;
      content: '';
      height: 12px;
      left: -18px;
      left: -6px;
      position: absolute;
      top: 3px;
      transform: rotate(45deg);
      width: 12px;
      z-index: 1;
    }

    &:after {
      background-color: transparent;
      border-bottom: 9px solid transparent;
      border-left: 9px solid var(--border);
      border-right: 9px solid transparent;
      border-top: 9px solid transparent;
      content: '';
      height: 0;
      position: absolute;
      right: -18px;
      top: 0;
      transition: border-color 0.8s ease;
      transition-delay: 0 0.4s;
      width: 0;
    }
  }

  .xp {
    color: var(--text);
    font-size: 12px;
    margin-left: 2px;
  }

  &.is-completed {
    .xp-wrapper {
      background-color: var(--success-base);
      transition: ${(props) => getTransitionBackgroundColor(props.delay)};

      &:before {
        background-color: var(--success-hover);
        transition: ${(props) => getTransitionBackgroundColor(props.delay)};
      }

      &:after {
        border-left: 9px solid var(--success-base);
        transition: ${(props) => getTransitionBorderColor(props.delay)};
      }

      .xp-tag-icon {
        color: var(--success-invert);
        transition: ${(props) => getTransitionColor(props.delay)};
        font-size: 9px;
        left: -5px;
        position: absolute;
        top: 1px;
        z-index: 3;
      }

      .xp {
        color: var(--success-invert);
        transition: ${(props) => getTransitionColor(props.delay)};
      }
    }
  }
`;

export default ProgressHexagon;
